<template>
  <Wrapper :style="pageStyle" @openEditor="openEditor">
    <div class="navigation-page">
      <div class="page-background" :style="backgroundStyle">
        <img v-if="backgroundImage" class="background-image" :src="backgroundImage"/>
        <div v-else class="background-color" :style="backgroundColor"></div>
      </div>
      <div class="menu-list">
        <div
          v-for="(menu, index) in menuList"
          class="menu"
          :class="[(index !== menuList.length - 1 || page.bottomLeftBtn?.show || page.bottomRightBtn?.show) && 'last-menu-has-line']"
          :key="getMenuId(menu)"
          @click="onMenuClick(menu)"
        >
          <span v-if="menu.label.html" v-html="menu.label.html" class="menu-label rich-text"></span>
          <span v-else class="menu-label">{{ menu.label.text }}</span>
        </div>
      </div>
      <div v-if="page === currentMenuPage" class="btn-list">
        <div class="bottom-btn">
          <div class="btn left-btn">
            <el-link v-if="page.bottomLeftBtn?.show" :underline="false" @click="onBottomBtnClick(page.bottomLeftBtn.link)">
              <span class="label">{{ page.bottomLeftBtn.label }}</span>
              <i class="el-icon-search el-icon--right"></i>
            </el-link>
          </div>
          <div class="btn right-btn">
            <el-link v-if="page.bottomRightBtn?.show" :underline="false" @click="onBottomBtnClick(page.bottomRightBtn.link)">
              <span class="label">{{ page.bottomRightBtn.label }}</span>
              <i class="el-icon-search el-icon--right"></i>
            </el-link>
          </div>
        </div>
      </div>
      <div v-if="page !== currentMenuPage" class="back-btn">
        <el-button class="btn" type="text" @click="onBackClick">
          <i class="el-icon-arrow-left"></i>
          <span>{{ $t('common.btn.back') }}</span>
        </el-button>
      </div>
    </div>
  </Wrapper>
</template>

<script>
import _ from 'lodash'
import Wrapper from '../../components/render/wrapper.vue'
import { mapGetters } from 'vuex'
import Clickoutside from 'element-ui/src/utils/clickoutside'
import RenderMixin from '../renderMixin'
import { appendSourceCode } from '@/utils/common'
import { mixedRouterPush } from '@/router/routes'

export default {
  mixins: [RenderMixin],
  directives: {
    Clickoutside
  },
  components: {
    Wrapper
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  props: {
    singlePage: {
      type: Boolean,
      default: true
    },
    page: {
      type: Object,
      required: true
    },
    color: {
      type: String,
      required: false
    },
    lineColor: {
      type: String,
      required: false
    },
    linePadding: {
      type: [String, Number],
      required: false
    },
    fontSize: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      nextMenuId: 1,
      menuIdMap: {},
      parentMap: {},
      backgroundDefault: {
        size: 'cover',
        repeat: 'no-repeat',
        attachment: 'fixed'
      },
      currentMenuPage: null
    }
  },
  computed: {
    ...mapGetters('microSite', ['isEditMode', 'isMobile', 'currentPage', 'pagePathPrefix']),
    menuIndex () {
      return _.chain(this.$route.query.menu_index).split('-').filter().map(index => parseInt(index) - 1).join('.menu.list.').value()
    },
    pageTitle () {
      const [title, menu] = this.getConfig(this.currentMenuPage, 'title')
      let titleText = null
      if (title && title.sameWithMenuLabel) {
        const label = menu.label
        titleText = _.chain(label.text).split('\n').first().value()
      } else {
        titleText = title?.text
      }
      return titleText
    },
    pageStyle () {
      const [layout] = this.getConfig(this.currentMenuPage, 'layout')
      return {
        'font-size': this.fontSize,
        '--font-size': this.fontSize,
        '--color': this.color,
        '--line-color': this.lineColor,
        '--line-padding': `${this.linePadding}px`,
        '--top-padding': layout?.topPadding && `${layout?.topPadding}px`,
        '--horizontal-padding': layout?.horizontalPadding && `${layout?.horizontalPadding}px`
      }
    },
    pageBackground () {
      const [background] = this.getConfig(this.currentMenuPage, 'background')
      return background
    },
    backgroundStyle () {
      return {}
    },
    backgroundImage () {
      return this.pageBackground?.image
    },
    backgroundColor () {
      return this.pageBackground?.color
    },
    menuList () {
      const menu = this.currentMenuPage.menu
      if (menu.isActive) {
        return menu.list
      }
      return []
    },
    btnList () {
      const [btn] = this.getConfig(this.currentMenuPage, 'btn')
      return btn?.list
    }
  },
  created () {

  },
  methods: {
    openEditor () {
      this.$emit('openEditor')
    },
    resetMenuMap (menuPage, parent) {
      if (!menuPage) {
        menuPage = this.page
        this.nextMenuId = 1
        this.menuIdMap = {}
        this.parentMap = {}
      }
      const menuId = this.nextMenuId
      this.nextMenuId += 1
      this.menuIdMap[menuId] = menuPage
      this.parentMap[menuId] = parent
      if (!menuPage.menu.isActive) {
        return
      }
      _.forEach(menuPage.menu.list, (menuItem) => {
        this.resetMenuMap(menuItem, menuPage)
      })
    },
    resetCurrentMenu () {
      if (this.singlePage || !this.menuIndex) {
        this.currentMenuPage = this.page
        return
      }
      this.currentMenuPage = _.get(this.page.menu.list, this.menuIndex)
    },
    getMenuId (menuPage) {
      return _.findKey(this.menuIdMap, menuPage)
    },
    getParentMenuPage (menuPage) {
      const menuId = this.getMenuId(menuPage)
      return this.parentMap[menuId]
    },
    getConfig (menuPage, keyPath) {
      let config = _.get(menuPage, keyPath)
      while (config && config.useParent) {
        const parent = this.getParentMenuPage(menuPage)
        const parentConfig = _.get(parent, keyPath)
        if (!parentConfig) {
          break
        }
        menuPage = parent
        config = parentConfig
      }
      return [config, menuPage]
    },
    openLink (link) {
      link.url = link.url.trim()
      if (link.url.startsWith('/v/onboarding/questionnaire')) {
        this.$eventBus.$emit('openOnboardingQuestionnairePage', link.openNewWindow)
        return
      }
      let targetUrl = null
      if (link.isInnerPage) {
        targetUrl = this.pagePathPrefix + link.url
      } else {
        targetUrl = link.url
      }
      targetUrl = appendSourceCode(targetUrl)
      if (link.openNewWindow) {
        window.open(targetUrl)
      } else {
        if (link.isInnerPage) {
          this.$router.push(targetUrl)
        } else {
          mixedRouterPush(targetUrl)
        }
      }
    },
    onMenuClick (menuPage) {
      const link = menuPage.link
      if (link?.isActive && link.url) {
        this.openLink(link)
      } else if (menuPage.menu.isActive && menuPage.menu.list.length > 0) {
        if (this.singlePage) {
          this.currentMenuPage = menuPage
        } else {
          if (menuPage === this.page) {
            this.$router.push({ query: { ...this.$route.query, menu_index: null } })
          } else {
            let parent = this.getParentMenuPage(menuPage)
            const indexList = []
            while (parent) {
              indexList.push(parent.menu.list.indexOf(menuPage) + 1)
              menuPage = parent
              parent = this.getParentMenuPage(menuPage)
            }
            const menuIndex = _.chain(indexList).reverse().join('-').value()
            this.$router.push({ query: { ...this.$route.query, menu_index: menuIndex } })
          }
        }
      }
    },
    onBottomBtnClick (link) {
      if (!link.url) {
        return
      }
      this.openLink(link)
    },
    onBackClick () {
      const parent = this.getParentMenuPage(this.currentMenuPage)
      this.onMenuClick(parent)
    }
  },
  watch: {
    menuIndex () {
      this.resetCurrentMenu()
    },
    page: {
      immediate: true,
      handler () {
        this.resetMenuMap()
        this.resetCurrentMenu()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.render-wrapper {
  width: 100vw;
  width: 100dvw;
  height: 100vh;
  height: 100dvh;
  padding: var(--top-padding, 60px) var(--horizontal-padding, 20px) 40px;
  overflow: hidden;
}
.navigation-page {
  height: 100%;
  color: var(--color, #333);
  margin: 0 auto;
  overflow: auto;
  word-break: break-word;
  .page-background {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    .background-image {
      display: block;
      width: 100%;
    }
    .background-color {
      width: 100%;
      height: 100%;
    }
  }
  .menu-list {
    width: max-content;
    max-width: 100%;
    position: relative;
    .menu {
      padding-bottom: var(--line-padding, 10px);
      border-bottom: 0;
      cursor: pointer;
      + .menu {
        margin-top: var(--line-padding, 10px);
      }
      .menu-label {
        &.rich-text {
          ::v-deep {
            > p {
              line-height: initial;
            }
            p {
              margin: 0;
            }
          }
        }
      }
    }
    .last-menu-has-line {
      border-bottom: 1px solid var(--line-color, #333);
    }
  }
  .btn-list {
    position: relative;
    padding-top: var(--line-padding, 10px);
    .bottom-btn {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .btn {
        .el-link {
          color: var(--color);
          font-size: var(--font-size);
        }
      }
    }
  }
  .back-btn {
    position: relative;
    .btn {
      color: var(--color);
      font-size: var(--font-size);
    }
  }
}
.site-common {
  .navigation-page {
    .page-background {
      position: absolute;
    }
  }
}
.site-mobile {
  &.site-previewer {
    .render-wrapper {
      width: 100%;
    }
    .navigation-page {
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
}
</style>
